import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-unified-portal',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './unified-portal.component.html',
  styleUrl: './unified-portal.component.scss'
})
export class UnifiedPortalComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  nav(url: any) {
    console.log(url);
    this.router.navigateByUrl(url)
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const portalCaseStudySchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-portal",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-portal",
          "name": "Unified Customer Engagement Portal for Insurance Domain – Enhancing Brand Consistency and Client Interaction – Case Study",
          "description": "Case study on how Kalpita Technologies developed a secure unified customer engagement portal for a top-tier US insurance provider featuring Power BI dashboards.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2025-01-28"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-portal#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies", "name": "Case Studies" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-portal", "name": "Unified Portal" } }
          ]
        },
        {
          "@type": "Article",
          "headline": "Unified Customer Engagement Portal for Insurance Domain – Enhancing Brand Consistency and Client Interaction",
          "articleBody": "A leading insurance provider in the United States faced challenges with inconsistent digital engagement across various business lines... Kalpita Technologies developed a secure, unified customer engagement portal — an end-to-end SaaS solution...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-01-28",
          "image": "https://www.kalpitatechnologies.com/assets/resources/casestudies/unified-portal/arrow-indicator.svg"
        },
        {
          "@type": "HowTo",
          "name": "How to Build a Unified Customer Engagement Portal for Insurance",
          "step": [
            { "@type": "HowToStep", "name": "Assess Gaps", "text": "Audit digital touchpoints to identify inconsistent brand messaging." },
            { "@type": "HowToStep", "name": "Design Architecture", "text": "Architect a centralized SaaS platform on Azure." },
            { "@type": "HowToStep", "name": "Implement Security", "text": "Deploy a SOC2 and GDPR-compliant document vault." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/unified-portal#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is the Unified Customer Engagement Portal?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It is a secure, end-to-end SaaS solution developed for a top-tier US insurance provider to centralize client-advisor communication."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(portalCaseStudySchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
